import { API_BASE_URL } from "@/config";
import PersonalInformation from "../elements/personal_information/PersonalInformation.vue";
import EmploymentInformation from "../elements/employment_information/EmploymentInformation.vue";
import CostInputGrid from "../elements/cost_input_grid/CostInputGrid.vue";
import BreadCrumb from "@/components/BreadCrumb/BreadCrumb.vue";
import ClientLogo from "@/components/ClientLogo/ClientLogo.vue";
import BankingInfo from "../elements/banking_info/BankingInfo.vue";
import IncomeInputGrid from "../elements/income_input_grid/IncomeInputGrid.vue";
import ApplicationIncome from "../elements/application_income/ApplicationIncome.vue";
import SupportDocument from "../elements/support_document/SupportDocument.vue";

import { MAIN_CONTROLLER } from "@/utils/main_controller.js";
import { useToast } from "@/components/Toast/useToast";

export default {
  name: "SygnioQuestions",
  mixins: [MAIN_CONTROLLER],
  components: {
    ClientLogo,
    BreadCrumb,
    EmploymentInformation,
    PersonalInformation,
    CostInputGrid,
    BankingInfo,
    IncomeInputGrid,
    ApplicationIncome,
    SupportDocument
  },
  setup() {
    const { showSuccess, showError } = useToast();
    return { showSuccess, showError };
  },
  data() {
    return {
      cards: [
        {
          title: "Personal Information",
          components: ["PersonalInformation"],
          is_valid: false
        },
        {
          title: "Employment Details",
          components: ["EmploymentInformation"],
          is_valid: false
        },
        {
          title: "Banking Information",
          components: ["BankingInfo"],
          is_valid: false
        },
        {
          title: "Applicant Income",
          components: ["ApplicationIncome"],
          is_valid: false
        },
        {
          title: "Income Details",
          components: ["IncomeInputGrid"],
          is_valid: false
        },
        {
          title: "Expense Information",
          components: ["CostInputGrid"],
          is_valid: false
        },
        {
          title: "Supporting Documents",
          components: ["SupportDocument"],
          is_valid: false
        },
      ],
      current_step: 0,
      loading: false,
      is_mobile: false,
      data_ready: false,
    };
  },
  computed: {
    is_first_step() {
      return this.current_step === 0;
    },
    is_last_step() {
      return this.current_step === this.cards.length - 1;
    },
    progress_percentage() {
      return (this.current_step / (this.cards.length - 1)) * 100;
    }
  },
  watch: {
    cards: {
      deep: true,
      handler(new_cards) {
        if (new_cards.every(card => card.is_valid)) {
          this.mark_as_complete();
        }
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.get_application_data();
      this.check_device_size();
    })
    
    window.addEventListener('resize', this.check_device_size);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.check_device_size);
  },
  methods: {
    check_device_size() {
      this.is_mobile = window.innerWidth < 768;
    },
    scroll_to_top(smooth = true) {
      const scroll_options = smooth ? { behavior: 'smooth', top: 0 } : { top: 0 };
      window.scrollTo(scroll_options);
    },
    prev_step() {
      if (this.current_step > 0) {
        // Set current card to is_valid false before moving to previous step
        this.cards[this.current_step].is_valid = false;
        this.current_step--;
        this.scroll_to_top();
      }
    },
    next_step() {
      this.loading = true;
      setTimeout(() => {
        if (this.current_step < this.cards.length - 1) {
          this.current_step++;
          this.scroll_to_top();
        }
        this.loading = false;
      }, 300);
    },
    show_validation_error() {
      // On mobile, use a smaller toast notification instead of a full modal
      if (this.is_mobile) {
        const toast = Swal.mixin({
          toast: true,
          position: 'bottom',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        });
        
        toast.fire({
          icon: 'error',
          title: 'Please complete all required fields'
        });
      } else {
        Swal.fire({
          title: "Validation Error",
          text: "Please complete all required fields correctly",
          icon: "error",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    },
    on_validity_change(is_valid) {
      // Update the validity state of the current step
      this.cards[this.current_step].is_valid = is_valid;
      this.next_step();
    },
    get_application_data() {
      this.loading = true;
      const token = localStorage.getItem("token");
      const api_url = `${API_BASE_URL}/verify_api/financial_information_api/get_application_data/`;

      fetch(api_url, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          workflow_id: this.workflow_id,
        })
      })
      .then(response => response.json())
      .then(response_data => {

        var data = response_data.data || {};

        // Check if the response is valid and contains the expected data
        if (response_data.status === "success") {
          var existing_data = data.existing_data || {};
          var documents = data.documents || [];

          // Check each section's completion status
          var is_married = existing_data.customerMaritalStatus === "Married";
          var employment_status = existing_data.customerEmploymentStatus || "";
          var needs_employer_info = !["Not Selected", "Student", "Self employed", "Pensioner", "Unemployed"].includes(employment_status);
          
          var personal_information_mapping = [
            "customerFirstName",
            "customerSurname",
            "customerIdType",
            "customerIdNumber",
            "customerDateOfBirth",
            "customerMaritalStatus",
            "customerResidentialAddressLine1",
            "customerGender",
            "customerTitle",
            "customerRaceEthnicGroup",
            "customerNationality",
            "customerPreferredContactMethod",
            "customerMobilePhoneNumber",
            "customerHomePhoneNumber",
            "customerWorkPhoneNumber",
            "customerEmail",
            "countryOfBirth",
            "customerResidentialAddressSuburb",
            "customerResidentialAddressCity",
            "customerResidentialAddressPostalCode",
            "customerPeriodAtCurrentAddressYears",
            "customerPeriodAtCurrentAddressMonths"
          ]

          // Check Personal Information section
            var personal_info_complete = personal_information_mapping.every(
              field => !!existing_data[field]
            );
          
          // Check Employment section
          let employment_complete = true;
          if (needs_employer_info) {
            employment_complete = 
              !!existing_data.employerName && 
              !!existing_data.employerAddressLine1 && 
              !!existing_data.customerEmploymentStatus;
          } else {
            employment_complete = !!existing_data.customerEmploymentStatus;
          }
          
          // Check Banking Information
            var banking_complete = 
            !!existing_data.customerBankAccountBank &&
            !!existing_data.customerBankAccountHolder &&
            !!existing_data.customerBankAccountNumber &&
            !!existing_data.customerBankAccountType &&
            !!existing_data.customerSalaryDay;
          
          // Check Applicant Income
          let income_complete = 
            !!existing_data.sourceOfIncome && 
            (existing_data.sumIncomeCustomerGrossRemuneration !== undefined) && 
            (existing_data.addIncomeCustomerNetTakeHomePay !== undefined);
            
          if (is_married) {
            income_complete = income_complete && 
              (existing_data.sumIncomeSpouseGrossRemuneration !== undefined) && 
              (existing_data.addIncomeSpouseNetTakeHomePay !== undefined);
          }
          
          // Check Income Details
          var income_details_complete = 'sourceOfFundsBonus' in existing_data;
          
          // Check Expense Information
          var expense_complete = 'sumExpensesCustomerBondPayment' in existing_data;
          
          // Check Supporting Documents
          let docs_complete = false;
          if (documents && documents.length > 0) {
            const existing_doc_types = documents.map(doc => doc.document_type || '');
            
            // Check if at least one ID type document exists (IDBOOK, IDCARD, or DRIVERSLICENSE)
            const has_id_document = 
              existing_doc_types.includes('IDBOOK') || 
              existing_doc_types.includes('IDCARD') || 
              existing_doc_types.includes('DRIVERSLICENSE');
              
            // Check for bank statements, payslip, and proof of residence
            const has_bank_statement = existing_doc_types.includes('BANKSTATEMENTS');
            const has_payslip = existing_doc_types.includes('PAYSLIP');
            const has_proof_of_residence = existing_doc_types.includes('PROOFOFRESIDENCE');
            
            // If married, also check for marriage certificate
            let has_marriage_cert = !is_married; // Default to true if not married
            if (is_married) {
              has_marriage_cert = existing_doc_types.includes('MARRIAGECERTIFICATE');
            }
            
            docs_complete = has_id_document && has_bank_statement && has_payslip && has_proof_of_residence && has_marriage_cert;
          }
          
          // Update card validity status
          this.cards[0].is_valid = personal_info_complete;
          this.cards[1].is_valid = employment_complete;
          this.cards[2].is_valid = banking_complete;
          this.cards[3].is_valid = income_complete;
          this.cards[4].is_valid = income_details_complete;
          this.cards[5].is_valid = expense_complete;
          this.cards[6].is_valid = docs_complete;
          
          // Find first incomplete section and set as current step
          const first_incomplete_index = this.cards.findIndex(card => !card.is_valid);
          if (first_incomplete_index !== -1) {
            this.current_step = first_incomplete_index;
          }
        }
      })
      .finally(() => {
        this.loading = false;
        this.data_ready = true; // Set data_ready to true after fetching data
      });
    },
    mark_as_complete() {
      // Use a mobile-optimized confirmation dialog
      const options = this.is_mobile ? {
        title: "Complete Application?",
        text: "You won't be able to edit it later.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        reverseButtons: true,
        heightAuto: false,
        customClass: {
          container: 'mobile-swal-container',
          popup: 'mobile-swal-popup'
        }
      } : {
        title: "Are you sure?",
        text: "This will complete the application and you cannot continue later.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, complete it",
        cancelButtonText: "No, keep editing",
        reverseButtons: true
      };
      
      Swal.fire(options).then((result) => {
        if (result.isConfirmed) {
          this.loading = true;
          const token = localStorage.getItem("token");
          const complete_api_url = `${API_BASE_URL}/verify_api/financial_information_api/sygnio_answer_completed/`;

          $.ajax({
            type: "POST",
            url: complete_api_url,
            headers: {
              Authorization: `Token ${token}`,
            },
            data: JSON.stringify({
              workflow_id: this.workflow_id,
            }),
            contentType: "application/json",
            success: (response) => {
              this.loading = false;
              if (response.status === "success") {
                Swal.fire({
                  title: "Success",
                  text: "Your application data is complete!",
                  icon: "success",
                  timer: 3000,
                  timerProgressBar: true,
                  showConfirmButton: false,
                  heightAuto: this.is_mobile ? false : true,
                }).then(() => {
                  this.fetch_current_service();
                });
              }
            },
            error: () => {
              this.loading = false;
              Swal.fire({
                title: "Error",
                text: "Failed to mark the application as complete, please try again later.",
                icon: "error",
                timer: 3000,
                timerProgressBar: true,
                showConfirmButton: false,
                heightAuto: this.is_mobile ? false : true,
              });
            },
          });
        }
        if (result.dismiss === Swal.DismissReason.cancel) {
          this.cards[this.cards.length - 1].is_valid = false;
        }
      });
    },
  },
};
